/**
 * Formatting and labels for the panel. Pure functions, no DOM: they can be tested in Node.
 */

/** The badge tones the design system knows how to paint. */
export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

const timeFormatter = new Intl.DateTimeFormat("es-MX", { hour: "2-digit", minute: "2-digit" });
const dayFormatter = new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short" });

/**
 * Reads the two date shapes the system handles.
 *
 * SQLite returns `"2026-08-06 06:23:55"` in UTC with no zone: `new Date()` would take it as
 * local time and shift the whole activity log. Meta uses `+0000` with no colon, which is
 * not valid ISO either. Both are normalised before the date is built.
 */
export type DateInput = string | Date | null | undefined;

export function parseDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;

  const raw = String(value).trim();
  if (!raw) return null;

  let normalized = raw.replace(" ", "T");
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  if (!/(?:Z|[+-]\d{2}:\d{2})$/i.test(normalized)) normalized += "Z";

  const parsed = new Date(normalized);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

/** Short date and time. An invalid input never breaks the interface. */
export function formatDate(value: DateInput): string {
  const parsed = parseDate(value);
  return parsed ? `${dayFormatter.format(parsed)} ${timeFormatter.format(parsed)}` : "Sin fecha";
}

/**
 * How long until a future moment, in words.
 *
 * The log shows comments waiting their turn, and a bare timestamp forces a subtraction in
 * one's head against the clock — in UTC, no less. "In 25 min" reads without thinking,
 * which is what it takes to decide whether it is worth bringing forward by hand.
 *
 * It rounds up: saying "in 1 min" when 61 seconds remain is better than saying "any moment
 * now" and having nothing happen for another minute.
 */
export function formatCountdown(value: DateInput, now: Date = new Date()): string {
  const parsed = parseDate(value);
  if (!parsed) return "";

  const seconds = Math.ceil((parsed.getTime() - now.getTime()) / 1000);
  if (seconds <= 0) return "en cuanto le toque";
  if (seconds < 60) return `en ${seconds} s`;

  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `en ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `en ${hours} h` : `en ${hours} h ${rest} min`;
}

/** The time alone, with whatever meridiem the locale uses — never concatenated by hand. */
export function formatTime(value: DateInput): string {
  const parsed = parseDate(value);
  return parsed ? timeFormatter.format(parsed) : "Sin fecha";
}

export function formatDay(value: DateInput): string {
  const parsed = parseDate(value);
  return parsed ? dayFormatter.format(parsed) : "Sin fecha";
}

const STATUS_LABELS: Record<string, string> = {
  processed: "Entregado",
  processed_dry_run: "Simulado",
  scheduled: "Programado",
  pending: "Pendiente",
  retry: "Reintentando",
  failed: "Falló",
  discarded: "Descartado",
  ignored_no_match: "Sin coincidencia",
  ignored_self: "Comentario propio",
  ignored_repeat_user: "Ya atendido",
};

const STATUS_TONES: Record<string, Tone> = {
  processed: "success",
  processed_dry_run: "success",
  pending: "warning",
  scheduled: "warning",
  retry: "warning",
  failed: "danger",
  // Grey and not red: we stopped trying on purpose, there is nothing to review.
  discarded: "neutral",
  expired: "neutral",
};

const CONVERSATION_STATUS_LABELS: Record<string, string> = {
  awaiting: "Esperando respuesta",
  completed: "Conversación completa",
  failed: "Se cortó",
  // Neither a failure nor an error: the product's configured response wait ran out.
  expired: "Sin respuesta a tiempo",
};

const CONVERSATION_STATUS_TONES: Record<string, Tone> = {
  awaiting: "warning",
  completed: "success",
  failed: "danger",
  expired: "neutral",
};

export function conversationStatusLabel(status: string): string {
  return CONVERSATION_STATUS_LABELS[status] ?? status;
}

export function conversationStatusTone(status: string): Tone {
  return CONVERSATION_STATUS_TONES[status] ?? "neutral";
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function statusTone(status: string): Tone {
  return STATUS_TONES[status] ?? "neutral";
}

export function truncate(value: unknown, maxLength: number): string {
  const text = String(value ?? "");
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

export function commentsLabel(count: unknown): string {
  const total = Number(count) || 0;
  return `${total} ${total === 1 ? "comentario" : "comentarios"}`;
}
